import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Activity,
  Layers,
  Search,
  X,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import type { ActivityTreeNode, ActivityTreeNodeType } from '../types';

interface ActivityTreeProps {
  data: ActivityTreeNode[];
  isLoading?: boolean;
  onNodeClick?: (node: ActivityTreeNode) => void;
  onNavigate?: (path: string, node: ActivityTreeNode) => void;
}

interface TreeNodeProps {
  node: ActivityTreeNode;
  level: number;
  expandedNodes: Set<number>;
  onToggle: (nodeId: number) => void;
  onNodeClick?: (node: ActivityTreeNode) => void;
  onNavigate?: (path: string, node: ActivityTreeNode) => void;
  searchQuery: string;
}

// Get icon based on node type
const getNodeIcon = (type: ActivityTreeNodeType, isExpanded: boolean) => {
  switch (type) {
    case 'head':
      return isExpanded ? (
        <FolderOpen className="h-5 w-5 text-blue-600" />
      ) : (
        <Folder className="h-5 w-5 text-blue-500" />
      );
    case 'subHead':
      return isExpanded ? (
        <FolderOpen className="h-4 w-4 text-purple-600" />
      ) : (
        <Folder className="h-4 w-4 text-purple-500" />
      );
    case 'activity':
      return <Activity className="h-4 w-4 text-green-600" />;
    case 'subActivity':
      return <FileText className="h-4 w-4 text-orange-500" />;
    default:
      return <Layers className="h-4 w-4 text-gray-500" />;
  }
};

// Get background and border colors based on node type
const getNodeStyles = (type: ActivityTreeNodeType, isHovered: boolean) => {
  const baseStyles = 'transition-all duration-200 rounded-lg';
  
  switch (type) {
    case 'head':
      return `${baseStyles} ${
        isHovered
          ? 'bg-blue-100 border-blue-300 shadow-md'
          : 'bg-blue-50 border-blue-200'
      } border-l-4 border-l-blue-500`;
    case 'subHead':
      return `${baseStyles} ${
        isHovered
          ? 'bg-purple-100 border-purple-300 shadow-md'
          : 'bg-purple-50 border-purple-200'
      } border-l-4 border-l-purple-500`;
    case 'activity':
      return `${baseStyles} ${
        isHovered
          ? 'bg-green-100 border-green-300 shadow-md'
          : 'bg-green-50 border-green-200'
      } border-l-4 border-l-green-500`;
    case 'subActivity':
      return `${baseStyles} ${
        isHovered
          ? 'bg-orange-100 border-orange-300 shadow-md'
          : 'bg-orange-50 border-orange-200'
      } border-l-4 border-l-orange-500`;
    default:
      return `${baseStyles} bg-gray-50 border-gray-200`;
  }
};

// Get type badge
const getTypeBadge = (type: ActivityTreeNodeType) => {
  const badges: Record<ActivityTreeNodeType, { label: string; className: string }> = {
    head: { label: 'Head', className: 'bg-blue-200 text-blue-800' },
    subHead: { label: 'Sub Head', className: 'bg-purple-200 text-purple-800' },
    activity: { label: 'Activity', className: 'bg-green-200 text-green-800' },
    subActivity: { label: 'Sub Activity', className: 'bg-orange-200 text-orange-800' },
  };
  
  const badge = badges[type] || { label: 'Unknown', className: 'bg-gray-200 text-gray-800' };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
      {badge.label}
    </span>
  );
};

// Parse meta to show available years/phases
const parseMetaInfo = (meta: string | null): string => {
  if (!meta) return '';
  try {
    const parsed = JSON.parse(meta);
    const years = Object.keys(parsed).sort();
    return years.join(', ');
  } catch {
    return '';
  }
};

// Highlight search text in title
const highlightText = (text: string, query: string) => {
  if (!query.trim()) return text;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-200 px-0.5 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Check if node or any child matches search
const nodeMatchesSearch = (node: ActivityTreeNode, query: string): boolean => {
  if (!query.trim()) return true;
  
  const lowerQuery = query.toLowerCase();
  if (node.title.toLowerCase().includes(lowerQuery)) return true;
  
  return node.children.some((child) => nodeMatchesSearch(child, query));
};

// Tree Node Component
const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  expandedNodes,
  onToggle,
  onNodeClick,
  onNavigate,
  searchQuery,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const metaInfo = parseMetaInfo(node.meta);
  
  // Filter visible children based on search
  const visibleChildren = useMemo(() => {
    if (!searchQuery.trim()) return node.children;
    return node.children.filter((child) => nodeMatchesSearch(child, searchQuery));
  }, [node.children, searchQuery]);

  // Check if this node matches search
  const matchesSearch =
    !searchQuery.trim() || node.title.toLowerCase().includes(searchQuery.toLowerCase());

  // Don't render if doesn't match and has no matching children
  if (searchQuery.trim() && !matchesSearch && visibleChildren.length === 0) {
    return null;
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggle(node.id);
    }
  };

  const handleNodeClick = () => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onNavigate && node.path) {
      onNavigate(node.path, node);
    }
  };

  const paddingLeft = level * 24;

  return (
    <div className="animate-fadeIn">
      {/* Node Header */}
      <div
        className={`flex items-center gap-2 p-3 cursor-pointer border ${getNodeStyles(
          node.type,
          isHovered
        )} mb-1`}
        style={{ marginLeft: paddingLeft }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleNodeClick}
      >
        {/* Expand/Collapse Button */}
        <button
          onClick={handleToggle}
          className={`flex-shrink-0 p-1 rounded hover:bg-white/50 transition-colors ${
            hasChildren ? 'visible' : 'invisible'
          }`}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-600" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-600" />
          )}
        </button>

        {/* Icon */}
        <div className="flex-shrink-0">{getNodeIcon(node.type, isExpanded)}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`font-medium truncate ${
                node.type === 'head' ? 'text-base' : 'text-sm'
              } ${!matchesSearch ? 'opacity-60' : ''}`}
            >
              {highlightText(node.title, searchQuery)}
            </span>
            {getTypeBadge(node.type)}
            {node.recurringType !== null && (
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  node.recurringType === 1
                    ? 'bg-cyan-200 text-cyan-800'
                    : 'bg-rose-200 text-rose-800'
                }`}
              >
                {node.recurringType === 1 ? 'Recurring' : 'Non-Recurring'}
              </span>
            )}
          </div>
          {metaInfo && (
            <div className="text-xs text-gray-500 mt-1">Years: {metaInfo}</div>
          )}
        </div>

        {/* Navigate Button */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {node.path && onNavigate && (
            <button
              onClick={handleNavigate}
              className="p-1.5 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
              title="Navigate to this module"
            >
              <ExternalLink className="h-4 w-4 text-gray-600" />
            </button>
          )}
        </div>

        {/* Children Count */}
        {hasChildren && (
          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
            {node.children.length}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="relative">
          {/* Connection Line */}
          <div
            className="absolute top-0 bottom-4 w-px bg-gray-300"
            style={{ left: paddingLeft + 18 }}
          />
          {visibleChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onNodeClick={onNodeClick}
              onNavigate={onNavigate}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main Activity Tree Component
const ActivityTree: React.FC<ActivityTreeProps> = ({
  data,
  isLoading = false,
  onNodeClick,
  onNavigate,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Toggle node expansion
  const handleToggle = useCallback((nodeId: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Expand all nodes
  const expandAll = useCallback(() => {
    const allIds = new Set<number>();
    const collectIds = (nodes: ActivityTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(data);
    setExpandedNodes(allIds);
  }, [data]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Expand nodes that match search
  const expandMatching = useCallback(() => {
    if (!searchQuery.trim()) return;
    
    const matchingIds = new Set<number>();
    const findMatching = (nodes: ActivityTreeNode[], parentIds: number[] = []) => {
      nodes.forEach((node) => {
        const newPath = [...parentIds, node.id];
        if (node.title.toLowerCase().includes(searchQuery.toLowerCase())) {
          parentIds.forEach((id) => matchingIds.add(id));
        }
        if (node.children && node.children.length > 0) {
          findMatching(node.children, newPath);
        }
      });
    };
    findMatching(data);
    setExpandedNodes(matchingIds);
  }, [data, searchQuery]);

  // Handle navigation
  const handleNavigate = useCallback(
    (path: string, node: ActivityTreeNode) => {
      if (onNavigate) {
        onNavigate(path, node);
      } else {
        // Default navigation based on node type
        const basePath = '/pm-shri';
        switch (node.type) {
          case 'head':
            navigate(`${basePath}/analytics/heads/H${node.id}`);
            break;
          case 'subHead':
            // Extract head from path
            const headId = path.split('/')[1];
            navigate(`${basePath}/analytics/heads/H${headId}/subheads/${node.id}`);
            break;
          case 'activity':
          case 'subActivity':
            // Navigate with full path context
            navigate(`${basePath}/activity/${node.id}`, {
              state: { path, node },
            });
            break;
          default:
            break;
        }
      }
    },
    [navigate, onNavigate]
  );

  // Count total nodes
  const totalNodes = useMemo(() => {
    let count = 0;
    const countNodes = (nodes: ActivityTreeNode[]) => {
      nodes.forEach((node) => {
        count++;
        if (node.children) countNodes(node.children);
      });
    };
    countNodes(data);
    return count;
  }, [data]);

  // Filter visible root nodes
  const visibleRoots = useMemo(() => {
    if (!searchQuery.trim()) return data;
    return data.filter((node) => nodeMatchesSearch(node, searchQuery));
  }, [data, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
        <span className="ml-3 text-gray-600">Loading activity tree...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Layers className="h-6 w-6" />
          PM SHRI Activity Tree
        </h2>
        <p className="text-blue-100 text-sm mt-1">
          Navigate through the complete activity hierarchy • {totalNodes} total items
        </p>
      </div>

      {/* Search & Controls */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value) expandMatching();
              }}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors flex items-center gap-2"
            >
              <ChevronDown className="h-4 w-4" />
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors flex items-center gap-2"
            >
              <ChevronRight className="h-4 w-4" />
              Collapse All
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-gray-600">Head</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-purple-500"></div>
            <span className="text-gray-600">Sub Head</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-gray-600">Activity</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-orange-500"></div>
            <span className="text-gray-600">Sub Activity</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-cyan-400"></div>
            <span className="text-gray-600">Recurring</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-rose-400"></div>
            <span className="text-gray-600">Non-Recurring</span>
          </div>
        </div>
      </div>

      {/* Tree Content */}
      <div className="p-4 max-h-[600px] overflow-y-auto">
        {visibleRoots.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium">No activities found</p>
            <p className="text-sm">Try a different search term</p>
          </div>
        ) : (
          visibleRoots.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              level={0}
              expandedNodes={expandedNodes}
              onToggle={handleToggle}
              onNodeClick={onNodeClick}
              onNavigate={handleNavigate}
              searchQuery={searchQuery}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityTree;
